/**
 * PagesPlugin.jsx - UPDATED WITH TRANSLATABLE PAGE TITLES
 * File: src/pages/plugins/PagesPlugin.jsx (BROKER ADMIN)
 */

import { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, EyeOff,
  Save, Globe, Eye, Check, AlertCircle, X, Loader, Languages
} from 'lucide-react';
import { pagesAPI, settingsAPI } from '../../services/api';
import MediaUploader from '../UI/MediaUploader';
import { useLocale } from '../../contexts/LocaleContext';

// ── Language helpers ──────────────────────────────────────────────────────────

const LANG_META = {
  en: { label: 'English',    flag: '🇬🇧', dir: 'ltr' },
  ar: { label: 'العربية',    flag: '🇸🇦', dir: 'rtl' },
  fr: { label: 'Français',   flag: '🇫🇷', dir: 'ltr' },
  es: { label: 'Español',    flag: '🇪🇸', dir: 'ltr' },
  de: { label: 'Deutsch',    flag: '🇩🇪', dir: 'ltr' },
  pt: { label: 'Português',  flag: '🇧🇷', dir: 'ltr' },
  tr: { label: 'Türkçe',     flag: '🇹🇷', dir: 'ltr' },
  ru: { label: 'Русский',    flag: '🇷🇺', dir: 'ltr' },
  zh: { label: '中文',        flag: '🇨🇳', dir: 'ltr' },
  ja: { label: '日本語',      flag: '🇯🇵', dir: 'ltr' },
  ko: { label: '한국어',      flag: '🇰🇷', dir: 'ltr' },
  hi: { label: 'हिंदी',       flag: '🇮🇳', dir: 'ltr' },
  ms: { label: 'Malay',      flag: '🇲🇾', dir: 'ltr' },
  id: { label: 'Indonesian', flag: '🇮🇩', dir: 'ltr' },
  th: { label: 'ไทย',        flag: '🇹🇭', dir: 'ltr' },
  vi: { label: 'Tiếng Việt', flag: '🇻🇳', dir: 'ltr' },
};

function getT(value, lang) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value[lang] || value.en || Object.values(value)[0] || '';
}

function setT(existing, lang, newVal) {
  if (typeof existing === 'string') return { en: existing, [lang]: newVal };
  if (typeof existing === 'object' && existing !== null && !Array.isArray(existing)) return { ...existing, [lang]: newVal };
  return { [lang]: newVal };
}

function isTranslatable(type) {
  return ['text', 'textarea', 'richtext'].includes(type);
}

// ── Block type definitions ────────────────────────────────────────────────────

const FULL_WIDTH_FIELD = {
  key: 'full_width',
  label: 'Section Width',
  type: 'select',
  options: [
    { value: 'false', label: 'Contained (centered, max-width)' },
    { value: 'true',  label: 'Full Width (edge-to-edge)' },
  ],
};

const BLOCK_TYPES = {
  hero: {
    label: 'Hero Section',
    description: 'Large banner — multiple layouts available',
    icon: '🎯',
    defaultData: {
      layout: 'centered', full_width: false,
      heading: { en: "Trade the World's Markets" },
      subheading: { en: 'Access thousands of instruments across Forex, Crypto, Stocks and more.' },
      cta_text: { en: 'Open Account' }, cta_link: '/register',
      cta_secondary_text: { en: 'Try Demo' }, cta_secondary_link: '/register?demo=1',
      bg_color: '#0f172a', image_url: '', video_url: '', overlay_opacity: 0.5,
    },
    fields: [
      { key: 'layout', label: 'Layout', type: 'select', options: [
        { value: 'centered',    label: 'Centered text' },
        { value: 'image-right', label: 'Text left + Image right' },
        { value: 'image-left',  label: 'Image left + Text right' },
        { value: 'video-right', label: 'Text left + Video right' },
        { value: 'video-left',  label: 'Video left + Text right' },
        { value: 'fullscreen',  label: 'Fullscreen background image' },
      ]},
      { key: 'full_width', label: 'Full Width (edge-to-edge)', type: 'select',
        options: [{ value: false, label: 'No — constrained width' }, { value: true, label: 'Yes — full viewport width' }],
        showIf: d => ['image-right','image-left','video-right','video-left'].includes(d.layout),
      },
      { key: 'heading',            label: 'Main Heading',           type: 'text' },
      { key: 'subheading',         label: 'Subheading',             type: 'textarea' },
      { key: 'cta_text',           label: 'Primary Button Text',    type: 'text' },
      { key: 'cta_link',           label: 'Primary Button Link',    type: 'url' },
      { key: 'cta_secondary_text', label: 'Secondary Button Text',  type: 'text' },
      { key: 'cta_secondary_link', label: 'Secondary Button Link',  type: 'url' },
      { key: 'bg_color',           label: 'Background Color',       type: 'color' },
      { key: 'image_url', label: 'Image', type: 'url',
        showIf: d => ['image-right','image-left','fullscreen'].includes(d.layout) },
      { key: 'video_url', label: 'Video URL (YouTube / Vimeo / self-hosted)', type: 'url',
        showIf: d => ['video-right','video-left'].includes(d.layout) },
      { key: 'overlay_opacity', label: 'Overlay Darkness (0–1)', type: 'range', min: 0, max: 1, step: 0.1,
        showIf: d => d.layout === 'fullscreen' },
    ],
  },

  text: {
    label: 'Text Block', description: 'Heading + paragraph', icon: '📝',
    defaultData: { title: { en: 'Section Title' }, body: { en: 'Write your content here...' }, alignment: 'left' },
    fields: [
      { key: 'title',     label: 'Title',     type: 'text' },
      { key: 'body',      label: 'Body Text', type: 'textarea' },
      { key: 'alignment', label: 'Alignment', type: 'select',
        options: [{ value: 'left', label: 'Left' }, { value: 'center', label: 'Center' }, { value: 'right', label: 'Right' }] },
    ],
  },

  features_grid: {
    label: 'Features Grid', description: 'Grid of feature cards', icon: '⚡',
    defaultData: {
      title: { en: 'Why Choose Us' }, columns: '3',
      items: [
        { icon: '🚀', image_url: '', title: { en: 'Fast Execution' },    description: { en: 'Orders executed in milliseconds.' } },
        { icon: '🔒', image_url: '', title: { en: 'Secure & Regulated' }, description: { en: 'Your funds are protected.' } },
        { icon: '📊', image_url: '', title: { en: 'Advanced Analytics' }, description: { en: 'Professional-grade tools.' } },
      ],
    },
    fields: [
      { key: 'title',   label: 'Section Title', type: 'text' },
      { key: 'columns', label: 'Columns',       type: 'select',
        options: [{ value: '2', label: '2' }, { value: '3', label: '3' }, { value: '4', label: '4' }] },
      { key: 'items', label: 'Feature Cards', type: 'list', itemFields: [
        { key: 'icon',      label: 'Icon (emoji) — used if no image', type: 'icon' },
        { key: 'image_url', label: 'Image (overrides icon)',           type: 'url'  },
        { key: 'title',       label: 'Title',       type: 'text'     },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]},
    ],
  },

  stats: {
    label: 'Stats Bar', description: 'Row of key statistics', icon: '📊',
    defaultData: { bg_color: '', items: [
      { number: { en: '10,000+' }, label: { en: 'Active Traders' } },
      { number: { en: '500+' },    label: { en: 'Instruments' } },
      { number: { en: '99.9%' },   label: { en: 'Uptime' } },
      { number: { en: '24/7' },    label: { en: 'Support' } },
    ]},
    fields: [
      { key: 'bg_color', label: 'Background Color (empty = primary)', type: 'color' },
      { key: 'items', label: 'Stats', type: 'list', itemFields: [
        { key: 'number', label: 'Value', type: 'text' },
        { key: 'label',  label: 'Label', type: 'text' },
      ]},
    ],
  },

  steps: {
    label: 'Steps / How It Works', description: 'Numbered process steps', icon: '🔢',
    defaultData: { title: { en: 'How It Works' }, image_url: '', image_side: 'left', items: [
      { title: { en: 'Register' }, description: { en: 'Create your account in minutes.' } },
      { title: { en: 'Verify' },   description: { en: 'Complete a quick KYC check.' } },
      { title: { en: 'Trade' },    description: { en: 'Fund your account and start trading.' } },
    ]},
    fields: [
      { key: 'title',      label: 'Section Title',           type: 'text' },
      { key: 'image_url',  label: 'Section Image (optional)', type: 'url'  },
      { key: 'image_side', label: 'Image Side', type: 'select',
        options: [{ value: 'left', label: 'Image Left' }, { value: 'right', label: 'Image Right' }],
        showIf: d => d.image_url && d.image_url.trim() !== '',
      },
      { key: 'items', label: 'Steps', type: 'list', itemFields: [
        { key: 'title',       label: 'Step Title',  type: 'text'     },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]},
    ],
  },

  faq_accordion: {
    label: 'FAQ Accordion', description: 'Expandable Q&A', icon: '❓',
    defaultData: { title: { en: 'Frequently Asked Questions' }, items: [
      { question: { en: 'How do I open an account?' }, answer: { en: 'Click Open Account and complete registration.' } },
      { question: { en: 'What documents do I need?' }, answer: { en: 'A valid ID and proof of address.' } },
    ]},
    fields: [
      { key: 'title', label: 'Section Title', type: 'text' },
      { key: 'items', label: 'Questions', type: 'list', itemFields: [
        { key: 'question', label: 'Question', type: 'text'     },
        { key: 'answer',   label: 'Answer',   type: 'textarea' },
      ]},
    ],
  },

  cta_banner: {
    label: 'CTA Banner', description: 'Full-width call-to-action', icon: '📣',
    defaultData: { heading: { en: 'Ready to Start Trading?' }, subtext: { en: 'Open your account today.' }, button_text: { en: 'Get Started' }, button_link: '/register', bg_color: '#2563eb' },
    fields: [
      { key: 'heading',     label: 'Heading',          type: 'text'     },
      { key: 'subtext',     label: 'Subtext',          type: 'textarea' },
      { key: 'button_text', label: 'Button Text',      type: 'text'     },
      { key: 'button_link', label: 'Button Link',      type: 'url'      },
      { key: 'bg_color',    label: 'Background Color', type: 'color'    },
    ],
  },

  image_text: {
    label: 'Image + Text', description: 'Side-by-side image and text', icon: '🖼️',
    defaultData: { image_url: '', heading: { en: 'Section Heading' }, body: { en: 'Describe this section.' }, layout: 'image-left' },
    fields: [
      { key: 'image_url', label: 'Image',   type: 'url' },
      { key: 'heading',   label: 'Heading', type: 'text' },
      { key: 'body',      label: 'Body',    type: 'textarea' },
      { key: 'layout', label: 'Layout', type: 'select',
        options: [{ value: 'image-left', label: 'Image left' }, { value: 'image-right', label: 'Image right' }] },
    ],
  },

  video_embed: {
    label: 'Video Embed', description: 'YouTube or Vimeo', icon: '🎬',
    defaultData: { title: { en: '' }, video_url: '', caption: { en: '' } },
    fields: [
      { key: 'title',     label: 'Title',           type: 'text' },
      { key: 'video_url', label: 'Video Embed URL', type: 'url'  },
      { key: 'caption',   label: 'Caption',         type: 'text' },
    ],
  },

  testimonials: {
    label: 'Testimonials', description: 'Customer reviews', icon: '💬',
    defaultData: { title: { en: 'What Our Traders Say' }, items: [
      { name: 'John D.', role: { en: 'Forex Trader' }, quote: { en: 'Best platform I have used.' }, avatar_url: '' },
    ]},
    fields: [
      { key: 'title', label: 'Section Title', type: 'text' },
      { key: 'items', label: 'Testimonials', type: 'list', itemFields: [
        { key: 'name',       label: 'Name',       type: 'icon'     },
        { key: 'role',       label: 'Role',       type: 'text'     },
        { key: 'quote',      label: 'Quote',      type: 'textarea' },
        { key: 'avatar_url', label: 'Avatar URL', type: 'url'      },
      ]},
    ],
  },

  logo_cloud: {
    label: 'Logo Cloud', description: 'Partner/regulation logos', icon: '🏢',
    defaultData: { title: { en: 'Trusted By' }, items: [{ name: 'Partner 1', logo_url: '' }] },
    fields: [
      { key: 'title', label: 'Section Title', type: 'text' },
      { key: 'items', label: 'Logos', type: 'list', itemFields: [
        { key: 'name',     label: 'Name',     type: 'icon' },
        { key: 'logo_url', label: 'Logo URL', type: 'url'  },
      ]},
    ],
  },

  markets_table: {
    label: 'Markets Table', description: 'Markets you offer', icon: '📈',
    defaultData: { title: { en: 'Available Markets' }, items: [
      { market: { en: 'Forex' }, description: { en: 'Trade currency pairs' }, instruments: '50+', icon: '💱' },
    ]},
    fields: [
      { key: 'title', label: 'Section Title', type: 'text' },
      { key: 'items', label: 'Markets', type: 'list', itemFields: [
        { key: 'icon',        label: 'Icon',         type: 'icon'     },
        { key: 'market',      label: 'Market Name',  type: 'text'     },
        { key: 'description', label: 'Description',  type: 'textarea' },
        { key: 'instruments', label: 'Instruments',  type: 'icon'     },
      ]},
    ],
  },

  team_grid: {
    label: 'Team Grid', description: 'Team member cards', icon: '👥',
    defaultData: { title: { en: 'Meet Our Team' }, items: [{ name: 'John Smith', role: { en: 'CEO' }, photo_url: '', bio: { en: '' } }] },
    fields: [
      { key: 'title', label: 'Section Title', type: 'text' },
      { key: 'items', label: 'Members', type: 'list', itemFields: [
        { key: 'name',      label: 'Name',      type: 'icon'     },
        { key: 'role',      label: 'Role',      type: 'text'     },
        { key: 'photo_url', label: 'Photo URL', type: 'url'      },
        { key: 'bio',       label: 'Bio',       type: 'textarea' },
      ]},
    ],
  },

  contact_info: {
    label: 'Contact Info', description: 'Contact details', icon: '📬',
    defaultData: { email: 'support@yourbroker.com', phone: '+1 234 567 8900', address: { en: '123 Finance St' }, support_hours: { en: 'Mon-Fri 9am-6pm UTC' }, map_embed_url: '' },
    fields: [
      { key: 'email',         label: 'Email',         type: 'url'      },
      { key: 'phone',         label: 'Phone',         type: 'url'      },
      { key: 'address',       label: 'Address',       type: 'textarea' },
      { key: 'support_hours', label: 'Support Hours', type: 'text'     },
      { key: 'map_embed_url', label: 'Map Embed URL', type: 'url'      },
    ],
  },

  rich_text: {
    label: 'Rich Text', description: 'Free-form / legal text', icon: '📄',
    defaultData: { content: { en: '## Terms\n\nYour content here...' } },
    fields: [{ key: 'content', label: 'Content (Markdown)', type: 'richtext' }],
  },

  documents: {
    label: 'Documents', description: 'PDF / file download grid', icon: '📁',
    defaultData: {
      title: { en: 'Documents' }, description: { en: '' },
      items: [{ title: { en: 'Terms of Service' }, description: { en: 'Last updated January 2026' }, file_url: '', file_size: '' }],
    },
    fields: [
      { key: 'title',       label: 'Section Title',       type: 'text'     },
      { key: 'description', label: 'Section Description', type: 'textarea' },
      { key: 'items', label: 'Documents', type: 'list', itemFields: [
        { key: 'title',       label: 'Document Title',         type: 'text'     },
        { key: 'description', label: 'Description',            type: 'textarea' },
        { key: 'file_url',    label: 'File (PDF / DOC)',       type: 'url'      },
        { key: 'file_size',   label: 'File Size (e.g. 2.4 MB)', type: 'icon'   },
      ]},
    ],
  },

  partner_info: {
    label: 'Partner Info', description: 'Affiliate program CTA', icon: '🤝',
    defaultData: { title: { en: 'Partner With Us' }, description: { en: 'Earn commissions.' }, commission: { en: 'Up to $500' }, cta_text: { en: 'Become a Partner' }, cta_link: '/contact', benefits: [{ en: 'Competitive commissions' }] },
    fields: [
      { key: 'title',       label: 'Title',       type: 'text'     },
      { key: 'description', label: 'Description', type: 'textarea' },
      { key: 'commission',  label: 'Commission',  type: 'text'     },
      { key: 'cta_text',    label: 'Button Text', type: 'text'     },
      { key: 'cta_link',    label: 'Button Link', type: 'url'      },
      { key: 'benefits',    label: 'Benefits',    type: 'tags'     },
    ],
  },

  security_features: {
    label: 'Security Features', description: 'Security details', icon: '🔒',
    defaultData: { title: { en: 'Your Security is Our Priority' }, items: [
      { icon: '🔐', title: { en: '256-bit SSL' }, description: { en: 'All data encrypted.' } },
    ]},
    fields: [
      { key: 'title', label: 'Section Title', type: 'text' },
      { key: 'items', label: 'Features', type: 'list', itemFields: [
        { key: 'icon',        label: 'Icon',        type: 'icon'     },
        { key: 'title',       label: 'Title',       type: 'text'     },
        { key: 'description', label: 'Description', type: 'textarea' },
      ]},
    ],
  },

  spacer: {
    label: 'Spacer', description: 'Empty space', icon: '↕️',
    defaultData: { height: '60' },
    fields: [{ key: 'height', label: 'Height (px)', type: 'url' }],
  },

  divider: {
    label: 'Divider', description: 'Horizontal separator', icon: '➖',
    defaultData: { style: 'line' },
    fields: [{ key: 'style', label: 'Style', type: 'select',
      options: [{ value: 'line', label: 'Line' }, { value: 'dots', label: 'Dots' }, { value: 'gradient', label: 'Gradient' }] }],
  },
};

// ── Language Switcher ─────────────────────────────────────────────────────────
function LanguageSwitcher({ languages, current, onChange }) {
  if (languages.length <= 1) return null;
  return (
    <div className="flex items-center gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--bg-primary)', border: '1px solid var(--border-color)' }}>
      <Languages className="w-3.5 h-3.5 ml-1.5" style={{ color: 'var(--text-tertiary)' }} />
      {languages.map(lang => {
        const meta = LANG_META[lang] || { label: lang, flag: '🌐' };
        return (
          <button key={lang} onClick={() => onChange(lang)}
            className="px-2 py-1 rounded text-xs font-semibold transition-all"
            style={{ backgroundColor: current === lang ? 'var(--accent-primary)' : 'transparent', color: current === lang ? '#fff' : 'var(--text-secondary)' }}
            title={meta.label}>
            {meta.flag} {lang.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

// ── Field Input (i18n-aware) ──────────────────────────────────────────────────
function FieldInput({ field, value, onChange, lang, languages }) {
  const { t } = useLocale();
  const translatable = isTranslatable(field.type);
  const displayValue = translatable ? getT(value, lang) : (value || '');
  const handleChange = (newVal) => translatable ? onChange(setT(value, lang, newVal)) : onChange(newVal);
  const inputStyle = { backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)', direction: translatable && LANG_META[lang]?.dir === 'rtl' ? 'rtl' : 'ltr' };
  const missingLangs = translatable && languages.length > 1 ? languages.filter(l => !getT(value, l)) : [];
  const missingText = missingLangs.length > 0 ? `${t('pages.missing')}: ${missingLangs.map(l => l.toUpperCase()).join(', ')}` : null;

  if (field.type === 'text') return (<div><input value={displayValue} onChange={e => handleChange(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm border outline-none" style={inputStyle} placeholder={`${field.label} (${lang.toUpperCase()})`} />{missingText && <p className="text-[10px] mt-0.5" style={{ color: 'var(--accent-warning)' }}>{missingText}</p>}</div>);
  if (field.type === 'textarea') return (<div><textarea value={displayValue} onChange={e => handleChange(e.target.value)} rows={3} className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none" style={inputStyle} placeholder={`${field.label} (${lang.toUpperCase()})`} />{missingText && <p className="text-[10px] mt-0.5" style={{ color: 'var(--accent-warning)' }}>{missingText}</p>}</div>);
  if (field.type === 'richtext') return (<div><textarea value={displayValue} onChange={e => handleChange(e.target.value)} rows={12} className="w-full px-3 py-2 rounded-lg text-sm border outline-none font-mono resize-y" style={inputStyle} />{missingText && <p className="text-[10px] mt-0.5" style={{ color: 'var(--accent-warning)' }}>{missingText}</p>}</div>);
  if (field.type === 'url') {
    const mediaKeys = ['image_url', 'photo_url', 'avatar_url', 'logo_url', 'video_url'];
    const docKeys   = ['file_url'];
    if (mediaKeys.includes(field.key)) { const accept = field.key.includes('video') ? 'video' : 'image'; return <MediaUploader value={value || ''} onChange={onChange} accept={accept} />; }
    if (docKeys.includes(field.key))   return <MediaUploader value={value || ''} onChange={onChange} accept="document" />;
    return (<input value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm border outline-none" style={inputStyle} placeholder={field.label} />);
  }
  if (field.type === 'icon')  return (<input value={value || ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm border outline-none" style={inputStyle} placeholder={field.label} />);
  if (field.type === 'color') return (<div className="flex items-center gap-3"><input type="color" value={value || '#000000'} onChange={e => onChange(e.target.value)} className="w-10 h-10 rounded cursor-pointer border-0" /><input value={value || ''} onChange={e => onChange(e.target.value)} className="flex-1 px-3 py-2 rounded-lg text-sm border outline-none" style={inputStyle} /></div>);
  if (field.type === 'range') return (<div className="flex items-center gap-3"><input type="range" min={field.min||0} max={field.max||1} step={field.step||0.1} value={value||0} onChange={e => onChange(parseFloat(e.target.value))} className="flex-1" /><span className="text-xs font-mono w-10 text-right" style={{ color: 'var(--text-secondary)' }}>{value||0}</span></div>);
  if (field.type === 'select') return (<select value={value ?? ''} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg text-sm border outline-none" style={inputStyle}>{(field.options||[]).map(o => { const val = typeof o === 'string' ? o : o.value; const label = typeof o === 'string' ? o : o.label; return <option key={String(val)} value={String(val)}>{label}</option>; })}</select>);
  if (field.type === 'tags') return (<div className="space-y-2">{(value||[]).map((tag, i) => { const tagDisplay = translatable ? getT(tag, lang) : (typeof tag === 'string' ? tag : ''); return (<div key={i} className="flex gap-2"><input value={tagDisplay} onChange={e => { const arr = [...(value||[])]; if (translatable) arr[i] = setT(tag, lang, e.target.value); else arr[i] = e.target.value; onChange(arr); }} className="flex-1 px-3 py-2 rounded-lg text-sm border outline-none" style={inputStyle} /><button onClick={() => onChange((value||[]).filter((_,j) => j !== i))} className="p-2 rounded-lg hover:bg-red-500/10"><X className="w-4 h-4 text-red-400" /></button></div>); })}<button onClick={() => onChange([...(value||[]), translatable ? { [lang]: '' } : ''])} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-dashed" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}><Plus className="w-3 h-3" /> {t('pages.add')}</button></div>);
  if (field.type === 'list') return (<div className="space-y-3">{(value||[]).map((item, i) => (<div key={i} className="rounded-lg border p-3 space-y-2" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-primary)' }}><div className="flex items-center justify-between mb-1"><span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{t('pages.item')} {i+1}</span><button onClick={() => onChange((value||[]).filter((_,j) => j !== i))} className="p-1 rounded hover:bg-red-500/10"><X className="w-3.5 h-3.5 text-red-400" /></button></div>{field.itemFields.map(f => (<div key={f.key}><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{f.label}</label><FieldInput field={f} value={item[f.key]} lang={lang} languages={languages} onChange={v => { const arr = [...(value||[])]; arr[i] = { ...arr[i], [f.key]: v }; onChange(arr); }} /></div>))}</div>))}<button onClick={() => { const newItem = Object.fromEntries(field.itemFields.map(f => [f.key, isTranslatable(f.type) ? { [lang]: '' } : ''])); onChange([...(value||[]), newItem]); }} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-dashed w-full justify-center" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}><Plus className="w-3 h-3" /> {t('pages.addItem')}</button></div>);
  return null;
}

// ── Block Card ────────────────────────────────────────────────────────────────
function BlockCard({ block, index, total, onUpdate, onDelete, onMove, lang, languages }) {
  const [expanded, setExpanded] = useState(index === 0);
  const def = BLOCK_TYPES[block.type];
  if (!def) return null;

  return (
    <div className="rounded-xl border transition-all" style={{ borderColor: expanded ? 'var(--accent-primary)' : 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
      <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        <div className="text-lg select-none">{def.icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{def.label}</div>
          {!expanded && <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{getT(block.data?.heading, lang) || getT(block.data?.title, lang) || def.description}</div>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={e => { e.stopPropagation(); onMove(index, -1); }} disabled={index === 0} className="p-1.5 rounded hover:bg-[var(--bg-primary)] disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} /></button>
          <button onClick={e => { e.stopPropagation(); onMove(index, 1); }} disabled={index === total - 1} className="p-1.5 rounded hover:bg-[var(--bg-primary)] disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} /></button>
          <button onClick={e => { e.stopPropagation(); onDelete(block.id); }} className="p-1.5 rounded hover:bg-red-500/10"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
          {[FULL_WIDTH_FIELD, ...def.fields].map(field => {
            if (field.showIf && !field.showIf(block.data || {})) return null;
            return (
              <div key={field.key} className="pt-3">
                <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  {field.label}
                  {isTranslatable(field.type) && languages.length > 1 && <span className="ml-1.5 text-[10px] font-normal" style={{ color: 'var(--accent-primary)' }}>🌐</span>}
                </label>
                <FieldInput field={field} value={block.data?.[field.key]} lang={lang} languages={languages} onChange={v => onUpdate(block.id, { ...block.data, [field.key]: v })} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Block Picker ──────────────────────────────────────────────────────────────
function BlockPicker({ onAdd, onClose }) {
  const { t } = useLocale();

  const categories = {
    [t('pages.catHeroLayout')]:    ['hero', 'spacer', 'divider'],
    [t('pages.catContent')]:       ['text', 'rich_text', 'image_text', 'video_embed'],
    [t('pages.catFeaturesData')]:  ['features_grid', 'stats', 'steps', 'markets_table'],
    [t('pages.catSocialProof')]:   ['testimonials', 'logo_cloud', 'team_grid'],
    [t('pages.catCta')]:           ['cta_banner', 'partner_info', 'contact_info'],
    [t('pages.catDocuments')]:     ['documents'],
    [t('pages.catOther')]:         ['faq_accordion', 'security_features'],
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border p-5 max-h-[80vh] overflow-y-auto" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{t('pages.addBlock')}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-[var(--bg-primary)]"><X className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} /></button>
        </div>
        {Object.entries(categories).map(([catName, types]) => (
          <div key={catName} className="mb-4">
            <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>{catName}</p>
            <div className="grid grid-cols-1 gap-1.5">
              {types.filter(t => BLOCK_TYPES[t]).map(type => {
                const def = BLOCK_TYPES[type];
                return (
                  <button key={type} onClick={() => { onAdd(type); onClose(); }} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border text-left transition-all hover:scale-[1.01]" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-primary)' }} onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-primary)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}>
                    <span className="text-xl">{def.icon}</span>
                    <div><div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{def.label}</div><div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{def.description}</div></div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page Editor ───────────────────────────────────────────────────────────────
function PageEditor({ pageKey, onBack, languages }) {
  const { t } = useLocale();
  const [page,        setPage]        = useState(null);
  const [blocks,      setBlocks]      = useState([]);
  const [seo,         setSeo]         = useState({ title: '', description: '', slug: '' });
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [publishing,  setPublishing]  = useState(false);
  const [showPicker,  setShowPicker]  = useState(false);
  const [saveStatus,  setSaveStatus]  = useState(null);
  const [tab,         setTab]         = useState('blocks');
  const [lang,        setLang]        = useState(languages[0] || 'en');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await pagesAPI.getOne(pageKey);
        if (data.success) {
          const p = data.data;
          if (typeof p.content === 'string') p.content = JSON.parse(p.content);
          if (typeof p.title === 'string') p.title = { en: p.title };
          setPage(p);
          setBlocks(p.content?.blocks || []);
          setSeo({ title: p.seo_title || '', description: p.seo_description || '', slug: p.slug || '' });
        }
      } catch (e) { console.error('Load page failed:', e); }
      finally { setLoading(false); }
    })();
  }, [pageKey]);

  const generateId  = () => `block_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
  const addBlock    = useCallback(type => { const def = BLOCK_TYPES[type]; setBlocks(prev => [...prev, { id: generateId(), type, data: JSON.parse(JSON.stringify(def.defaultData)) }]); }, []);
  const updateBlock = useCallback((id, data) => { setBlocks(prev => prev.map(b => b.id === id ? { ...b, data } : b)); }, []);
  const deleteBlock = useCallback(id => { setBlocks(prev => prev.filter(b => b.id !== id)); }, []);
  const moveBlock   = useCallback((index, dir) => { setBlocks(prev => { const a = [...prev]; const tgt = index + dir; if (tgt < 0 || tgt >= a.length) return a; [a[index], a[tgt]] = [a[tgt], a[index]]; return a; }); }, []);

  const save = async () => {
    setSaving(true); setSaveStatus(null);
    try {
      const data = await pagesAPI.update(pageKey, { title: page?.title, slug: seo.slug, content: { blocks }, seo_title: seo.title, seo_description: seo.description });
      if (data.success) { setPage(data.data); setSaveStatus('saved'); setTimeout(() => setSaveStatus(null), 3000); }
      else setSaveStatus('error');
    } catch { setSaveStatus('error'); }
    finally { setSaving(false); }
  };

  const togglePublish = async () => {
    setPublishing(true);
    try {
      const data = page?.is_published ? await pagesAPI.unpublish(pageKey) : await pagesAPI.publish(pageKey);
      if (data.success) setPage(p => ({ ...p, is_published: !p?.is_published }));
    } catch (e) { console.error('Publish failed:', e); }
    finally { setPublishing(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader className="w-6 h-6 animate-spin" style={{ color: 'var(--accent-primary)' }} /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-[var(--bg-secondary)]"><ArrowLeft className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} /></button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-black" style={{ color: 'var(--text-primary)' }}>{getT(page?.title, lang)}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <code className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>{page?.slug}</code>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${page?.is_published ? 'bg-green-500/15 text-green-500' : 'bg-yellow-500/15 text-yellow-500'}`}>
              {page?.is_published ? t('pages.published') : t('pages.draft')}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {saveStatus === 'saved' && <span className="flex items-center gap-1 text-xs text-green-500 font-semibold"><Check className="w-3.5 h-3.5" /> {t('pages.saved')}</span>}
          {saveStatus === 'error' && <span className="flex items-center gap-1 text-xs text-red-400 font-semibold"><AlertCircle className="w-3.5 h-3.5" /> {t('pages.error')}</span>}
          <button onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white disabled:opacity-60" style={{ backgroundColor: 'var(--accent-primary)' }}>
            {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {t('pages.save')}
          </button>
          <button onClick={togglePublish} disabled={publishing} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold border disabled:opacity-60 ${page?.is_published ? 'text-yellow-500 border-yellow-500/30 hover:bg-yellow-500/10' : 'text-green-500 border-green-500/30 hover:bg-green-500/10'}`}>
            {publishing ? <Loader className="w-4 h-4 animate-spin" /> : page?.is_published ? <EyeOff className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
            {page?.is_published ? t('pages.unpublish') : t('pages.publish')}
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex gap-1 p-1 rounded-xl flex-1" style={{ backgroundColor: 'var(--bg-secondary)' }}>
          {[
            { id: 'blocks', label: `🧱 ${t('pages.tabBlocks')}` },
            { id: 'seo',    label: `🔍 ${t('pages.tabSeo')}` },
          ].map(({ id, label }) => (
            <button key={id} onClick={() => setTab(id)} className="flex-1 py-2 rounded-lg text-sm font-semibold"
              style={{ backgroundColor: tab === id ? 'var(--accent-primary)' : 'transparent', color: tab === id ? 'white' : 'var(--text-secondary)' }}>
              {label}
            </button>
          ))}
        </div>
        <LanguageSwitcher languages={languages} current={lang} onChange={setLang} />
      </div>

      {tab === 'blocks' && (
        <div className="space-y-3">
          {blocks.length === 0 && (
            <div className="rounded-2xl border-2 border-dashed p-12 text-center" style={{ borderColor: 'var(--border-color)' }}>
              <div className="text-4xl mb-3">🧱</div>
              <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{t('pages.noBlocks')}</div>
              <div className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>{t('pages.addFirstBlockHint')}</div>
              <button onClick={() => setShowPicker(true)} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
                <Plus className="w-4 h-4" /> {t('pages.addFirstBlock')}
              </button>
            </div>
          )}
          {blocks.map((block, i) => (<BlockCard key={block.id} block={block} index={i} total={blocks.length} onUpdate={updateBlock} onDelete={deleteBlock} onMove={moveBlock} lang={lang} languages={languages} />))}
          {blocks.length > 0 && (
            <button onClick={() => setShowPicker(true)} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed text-sm font-semibold hover:bg-[var(--bg-secondary)]" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}>
              <Plus className="w-4 h-4" /> {t('pages.addBlock')}
            </button>
          )}
        </div>
      )}

      {tab === 'seo' && (
        <div className="rounded-2xl border p-5 space-y-5" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
              {t('pages.pageTitle')}
              {languages.length > 1 && <span className="ml-1.5 text-[10px] font-normal" style={{ color: 'var(--accent-primary)' }}>🌐 {t('pages.translatable')}</span>}
            </label>
            <FieldInput field={{ key: 'title', label: t('pages.pageTitle'), type: 'text' }} value={page?.title} lang={lang} languages={languages} onChange={v => setPage(p => ({ ...p, title: v }))} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('pages.pageTitleHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('pages.pageSlug')}</label>
            <input value={seo.slug} onChange={e => setSeo(s => ({ ...s, slug: e.target.value }))} placeholder="/about-us" className="w-full px-3 py-2 rounded-lg text-sm border outline-none font-mono" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('pages.seoTitle')}</label>
            <input value={seo.title} onChange={e => setSeo(s => ({ ...s, title: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm border outline-none" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{seo.title.length}/60</p>
          </div>

          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>{t('pages.seoDescription')}</label>
            <textarea value={seo.description} onChange={e => setSeo(s => ({ ...s, description: e.target.value }))} rows={3} className="w-full px-3 py-2 rounded-lg text-sm border outline-none resize-none" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{seo.description.length}/160</p>
          </div>

          <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>{t('pages.googlePreview')}</p>
            <p className="text-sm font-semibold text-blue-400 truncate">{seo.title || getT(page?.title, lang)}</p>
            <p className="text-xs text-green-600 truncate">yourbroker.com{seo.slug}</p>
            <p className="text-xs mt-1 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>{seo.description || t('pages.noDescription')}</p>
          </div>
        </div>
      )}

      {showPicker && <BlockPicker onAdd={addBlock} onClose={() => setShowPicker(false)} />}
    </div>
  );
}

// ── Pages List ────────────────────────────────────────────────────────────────
export default function PagesPlugin() {
  const { t } = useLocale();
  const [pages,       setPages]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [editingKey,  setEditingKey]  = useState(null);
  const [pluginError, setPluginError] = useState(null);
  const [languages,   setLanguages]   = useState(['en']);
  const [adminLang,   setAdminLang]   = useState('en');

  const load = async () => {
    setLoading(true);
    try {
      const data = await pagesAPI.getAll();
      if (data.success) setPages(data.data);
      else if (data.code === 'PLUGIN_NOT_ACTIVE') setPluginError(data.error);
    } catch (e) {
      if (e.response?.data?.code === 'PLUGIN_NOT_ACTIVE') setPluginError(e.response.data.error);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    settingsAPI.getBroker().then(res => {
      const data = res.data || res;
      const langs = data.supported_languages || data.supportedLanguages;
      if (Array.isArray(langs) && langs.length > 0) { setLanguages(langs); setAdminLang(langs[0]); }
    }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, []);

  if (editingKey) return <PageEditor pageKey={editingKey} onBack={() => { setEditingKey(null); load(); }} languages={languages} />;

  if (pluginError) return (
    <div className="max-w-lg mx-auto text-center py-16 space-y-5">
      <div className="text-5xl">🌐</div>
      <h1 className="text-xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>{t('pages.websitePages')}</h1>
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('pages.pluginNotActive')}</p>
      <button onClick={() => window.location.href = '/broker/plugins'} className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white" style={{ backgroundColor: 'var(--accent-primary)' }}>
        {t('pages.viewPlugins')}
      </button>
    </div>
  );

  const mainPages  = pages.filter(p => p.category === 'main');
  const legalPages = pages.filter(p => p.category === 'legal');

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>{t('pages.websitePages')}</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{t('pages.websitePagesDesc')}</p>
        </div>
        {languages.length > 1 && (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <Languages className="w-3.5 h-3.5" style={{ color: 'var(--accent-primary)' }} />
              <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
                {languages.map(l => (LANG_META[l]?.flag || '🌐')).join(' ')} {languages.length} {t('pages.langs')}
              </span>
            </div>
            <LanguageSwitcher languages={languages} current={adminLang} onChange={setAdminLang} />
          </div>
        )}
      </div>
      {loading
        ? <div className="flex items-center justify-center py-16"><Loader className="w-6 h-6 animate-spin" style={{ color: 'var(--accent-primary)' }} /></div>
        : <>
            <PageGroup title={t('pages.mainPages')}  pages={mainPages}  onEdit={setEditingKey} lang={adminLang} />
            <PageGroup title={t('pages.legalPages')} pages={legalPages} onEdit={setEditingKey} lang={adminLang} />
          </>
      }
    </div>
  );
}

function PageGroup({ title, pages, onEdit, lang }) {
  const { t } = useLocale();
  return (
    <div className="space-y-2">
      <h2 className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>{title}</h2>
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
        {pages.map((page, i) => (
          <div key={page.key} className={`flex items-center gap-4 px-5 py-4 cursor-pointer transition-all hover:bg-[var(--bg-primary)] ${i > 0 ? 'border-t' : ''}`} style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }} onClick={() => onEdit(page.key)}>
            <span className="text-2xl">{page.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{getT(page.title, lang)}</div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{page.slug}{page.updated_at && ` · ${new Date(page.updated_at).toLocaleDateString()}`}</div>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${!page.exists ? 'bg-[var(--bg-primary)] text-[var(--text-muted)]' : page.is_published ? 'bg-green-500/15 text-green-500' : 'bg-yellow-500/15 text-yellow-500'}`}>
              {!page.exists ? t('pages.notStarted') : page.is_published ? `● ${t('pages.published')}` : `○ ${t('pages.draft')}`}
            </span>
            <ChevronDown className="w-4 h-4 -rotate-90" style={{ color: 'var(--text-muted)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}